import { selectCurrency } from '@/redux/config/configSlice';
import { selectActiveTicket } from '@/redux/data/dataSlice';
import { ItemType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ItemListItemProps {
  item: ItemType;
  onItemPress: (item: ItemType) => void;
}

const ItemListItem: FC<ItemListItemProps> = ({ item, onItemPress }) => {
  const { t, i18n } = useTranslation();
  const currency = useAppSelector(selectCurrency);
  const activeTicket = useAppSelector(selectActiveTicket);

  const lang = i18n.language;
  const isArabic = lang === 'ar';

  const activeItemQuantity = activeTicket?.items?.find((i) => i.ID === item.ID)?.quantity || 0;

  // Determine the display price based on priority:
  // 1. customerTierPriceAfterTax (if exists and customer is selected)
  // 2. priceAfterTax (if exists)
  // 3. price (fallback)
  const displayPrice = item.customerTierPriceAfterTax ?? item.priceAfterTax ?? item.price;

  return (
    <div
      onClick={() => onItemPress(item)}
      className="group flex flex-row items-center gap-3 cursor-pointer transition-all duration-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 rounded-lg p-3 m-1"
    >
      <div className="relative">
        {item.iconType === 'COLOR' && (
          <div
            className="w-12 h-12 rounded-lg shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow overflow-hidden flex-shrink-0"
            style={{ backgroundColor: item.iconValue }}
          />
        )}
        {item.iconType === 'IMAGE' && (
          <div className="w-12 h-12 rounded-lg overflow-hidden shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow flex-shrink-0">
            <img src={item.iconValue} alt={item.name} className="w-full h-full object-cover" />
          </div>
        )}
        {activeItemQuantity > 0 && (
          <div className="absolute -top-2 -right-2 min-w-[22px] h-[22px] flex items-center justify-center bg-blue-500 text-white text-sm font-semibold rounded-full px-1">
            {activeItemQuantity}
          </div>
        )}
      </div>
      <div className="flex flex-row min-h-[64px] py-1 items-center justify-between flex-1">
        <div className="flex-1 space-y-1.5">
          <div className="text-[15px] font-semibold text-gray-900 dark:text-white group-hover:text-frenchBlue dark:group-hover:text-white transition-colors duration-300">
            {isArabic && item.nameAr ? item.nameAr : item.name}
          </div>
          {item.stock !== null && (
            <div className="flex items-center">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400 dark:bg-green-300 mr-1.5" />
              <span className="text-[12px] text-gray-600 dark:text-gray-200">
                {item.stock} {t('common:in_stock')}
              </span>
            </div>
          )}
        </div>
        <div className="min-w-[65px] text-right ml-3 flex-shrink-0">
          {displayPrice && (
            <div className="bg-gray-100 dark:bg-gray-500 rounded-lg px-3 py-1.5">
              <span className="text-[14px] font-bold text-frenchBlue dark:text-white whitespace-nowrap">
                {displayPrice.toFixed(2)} {currency}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemListItem;
